import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { useUsersPage, useUsersStore } from '../state/users';
import { useState } from 'react';
import { Avatar } from './Avatar';

export const UserSearch = ({
  defaultValue,
  defaultOptions,
  onChange,
  isMulti = false,
  iconOnly = false
}: {
  defaultValue: string[];
  defaultOptions: ProjectUserInterface[];
  onChange: (value: any) => void;
  isMulti?: boolean;
  iconOnly?: boolean;
}) => {
  const { loadUsersPage } = useUsersPage();
  const { users } = useUsersStore();
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  let timeout:any;

  const searchForUsers = (_inputValue: string, callback: (options: any[]) => void): void => {
    clearTimeout(timeout);
    timeout = setTimeout(async () => {
      try {
        let usersList:ProjectUserInterface[] = users;
        if (usersList?.length === 0) {
          const usersRequest = await loadUsersPage(1);
          usersList = usersRequest?.results || [];
        }
  
        callback(usersList.map((user:ProjectUserInterface) => ({
          label: <div className='flex items-center gap-2'>
            <Avatar
              firstName={user.first_name}
              lastName={user.last_name}
              email={user.email}
              size={24}
            />
            {user.first_name} {user.last_name}
          </div>,
          value: user.id,
        })) as any);
        return {};
      } catch(e: Error | any) {
        toast.error('Failed to load users');
        callback([]);
        throw new Error(e);
      }
    }, 1000);
  }

  const handleChange = (e:any, userList: ProjectUserInterface[]) => {
    let user:any;
    if (Array.isArray(e)) {
      user = e.map(u => {
        return [...userList, ...defaultOptions].find(u2 => u2.id === u.value)
      });
    } else {
      user = [...userList, ...defaultOptions].find(u => u.id === e.value);
    }
    setSelectedValue(Array.isArray(e) ? user.map((us:any) => us.id) : [e.value]);
    onChange(user);
  }

  const selectedUsers = [...users, ...defaultOptions].filter(u => selectedValue.indexOf(u.id) > -1);
  const defaultValueObj = selectedUsers.map(u => ({ 
    label: iconOnly ? (
      <Avatar
        firstName={u.first_name}
        lastName={u.last_name}
        email={u.email}
        size={24}
      />
    ) : (
      <div className='flex items-center gap-2'>
        <Avatar
          firstName={u.first_name}
          lastName={u.last_name}
          email={u.email}
          size={24}
        />
        {u.first_name} {u.last_name}
      </div>
    ),
    value: u.id 
  }));

  return(
    <AsyncSelect
      required
      isMulti={isMulti}
      cacheOptions
      defaultOptions={
        defaultOptions.map(
          (u) => ({
            label: <div className='flex items-center gap-2'>
              <Avatar
                firstName={u.first_name}
                lastName={u.last_name}
                email={u.email}
                size={24}
              />
              {u.first_name} {u.last_name}
            </div>,
            value: u.id 
          })
        )
      }
      defaultValue={defaultValueObj}
      loadOptions={searchForUsers}
      onChange={(e) => handleChange(e, useUsersStore.getState().users)}
      styles={{
        control: (baseStyles, state) => ({
          ...baseStyles,
          border: iconOnly ? 'none' : '1px solid transparent',
          borderRadius: iconOnly ? '50%' : '6px',
          padding: iconOnly ? '2px' : '2px 8px',
          backgroundColor: 'transparent',
          minHeight: iconOnly ? '32px' : 'auto',
          width: iconOnly ? '32px' : 'auto',
          fontSize: '14px',
          fontWeight: '500',
          transition: 'all 0.2s ease',
          boxShadow: 'none',
          cursor: 'pointer',
          ...(!iconOnly && {
            '&:hover': {
              backgroundColor: 'rgba(249, 250, 251, 0.8)',
              borderColor: '#d1d5db',
            },
            ...(state.isFocused && {
              borderColor: '#3b82f6',
              backgroundColor: 'white',
              boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.1)',
            }),
          }),
        }),
        indicatorSeparator: () => ({ display: 'none' }),
        valueContainer: (base) => ({
          ...base,
          padding: iconOnly ? '0' : '0 4px',
          justifyContent: iconOnly ? 'center' : 'flex-start',
        }),
        dropdownIndicator: (base) => ({
          ...base,
          display: 'none',
        }),
        singleValue: (baseStyles) => ({
          ...baseStyles,
          margin: 0,
          position: 'static',
          transform: 'none',
        }),
        menu: (baseStyles) => ({
          ...baseStyles,
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden',
          marginTop: '4px',
          minWidth: iconOnly ? '200px' : '140px',
          zIndex: 9999,
        }),
        option: (baseStyles, state) => ({
          ...baseStyles,
          padding: '8px 12px',
          fontSize: '14px',
          fontWeight: '500',
          color: state.isSelected ? 'white' : '#374151',
          backgroundColor: state.isSelected 
            ? '#3b82f6' 
            : state.isFocused 
              ? '#f3f4f6' 
              : 'white',
          '&:hover': {
            backgroundColor: state.isSelected ? '#3b82f6' : '#f3f4f6',
          },
        }),
      }}
    />
  );
};